import {defineStore} from "pinia";
import type {NLocale} from "naive-ui/es/locales/common/enUS";
import type {NDateLocale} from "naive-ui/es/locales/date/enUS";
import {Language, languageToNaiveLocale, navigatorToEnum} from "@/constants/language";
import {getUserLanguage, updateUserLanguage} from "@/services/user/user-service";
import {type RemovableRef, useStorage} from "@vueuse/core";
import {languageStoreId} from "@/constants/stores";

interface LanguageState {
    currentLang: RemovableRef<Language>;
    currentLocale: NLocale;
    currentDateLocale: NDateLocale;
    loading: boolean;
    dateFormat: string;
    timeFormat: string;
    dateTimeFormat: string;
}

//Please note: this store is not cleared when logging out.
export const useLanguageStore = defineStore({
    id: languageStoreId,
    state: () => ({
        loading: false,
        currentLocale: languageToNaiveLocale(navigatorToEnum(navigator.language)).locale,
        currentDateLocale: languageToNaiveLocale(navigatorToEnum(navigator.language)).dateLocale,
        currentLang: useStorage("currentLang", navigatorToEnum(navigator.language)),
        dateFormat: "dd-MM-yyyy",
        timeFormat: "HH:mm",
        dateTimeFormat: "dd-MM-yyyy HH:mm",
    } as LanguageState),
    actions: {
        async switchLanguage(language: Language, userId: string, save: boolean = true) {
            this.setLocale(language);
            if (save) {
                await updateUserLanguage({id: userId, language: language as Language});
            }
        },
        setLocale(language: Language) {
            this.currentLang = language;
            const locales = languageToNaiveLocale(this.currentLang);
            this.currentLocale = locales.locale;
            this.currentDateLocale = locales.dateLocale;
        },
        async initCurrentLanguage(userId: string) {
            if (userId === "") {
                return Language.enUS;
            }

            //getUserLanguage api returns the language or english if an error occurs
            const valueFromApi = await getUserLanguage(userId);

            this.setLocale(valueFromApi);
        }
    }
});
